import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useIntl } from 'react-intl';

import Config from '~/config';
import { useAppDispatch, useAppSelector } from '~/hooks/redux';
import Button from '~/components/ui/Button';
import { events, useChat } from './ChatContext';
import { setOpeningPM } from '~/modules/chat';

import styles from './PrivateMessagesPane.scss';

function PrivateRoomTab({
  message,
  room,
  currentUsername,
  onOpenRoom,
}: {
  message: ChatMessage;
  room: ChatRoom;
  currentUsername: string;
  onOpenRoom: (room: ChatRoom) => void;
}) {
  const roomName = room.name.replace(currentUsername, '').replace(':', '').trim();

  return (
    <div className={styles.PrivateRoomTab}>
      <Button color="transparent" onClick={() => onOpenRoom(room)}>
        <span role="img" className="ion-chatbubble" />
        &nbsp;
        <span>{roomName}</span>
      </Button>

      {
        message.text && (
          <div className={styles.LastMessage}>
            <b className={styles.LastMessage__Username}>{message.user.username}:</b>
            {' '}
            {message.text}
          </div>
        )
      }
    </div>
  )
}

export default function PrivateMessagesPane({
  currentUser,
  setSidePaneOpen,
}: {
  currentUser: ChatUser | null;
  setSidePaneOpen: (pane: string | null) => void;
}) {
  const intl = useIntl();
  const chat = useChat();
  const dispatch = useAppDispatch();
  const auth = useAppSelector(state => state.auth);
  const { data, isLoading } = useQuery({
    queryKey: ['privateMessages'],
    queryFn: async () => {
      const response = await axios.get<ChatMessage[]>(`${Config.api}/users/me/messages`, {
        headers: {
          ...auth,
        },
      });
      return response.data;
    },
  });

  const onOpenRoom = (room: ChatRoom) => {
    chat?.client?.emit(events.JOIN_ROOM, {
      id: room.id,
    });

    dispatch(setOpeningPM(true));
    setSidePaneOpen('PRIVATE_MESSAGES');
  };

  return (
    <div className={styles.PrivateMessagesPane}>
      <div className={styles.PrivateMessagesPane__Header}>
        {intl.formatMessage({ id: 'Chat_RecentPmsHeader', defaultMessage: 'Recent PMs' })}
      </div>
      {isLoading && <p>Loading...</p>}
      {data && data.map(message => (
        <PrivateRoomTab
          key={message.room.id}
          room={message.room}
          currentUsername={currentUser?.username || ''}
          onOpenRoom={onOpenRoom}
          message={message}
        />
      ))}
    </div>
  )
}